// Munkatársak — a belső munkatársak (platform identity, type=internal) GLOBÁLIS címtára az ÉLŐ adatból, a KÖZÖS
// DataTable-re építve (a clients.index a referencia-minta). Szerver-oldali kereső (név/e-mail, 300 ms debounce),
// szerver-rendezés (név, magyar collation — csak az irány váltható), végtelen görgetés (100/lap, a belső létszám
// kicsi → gyakorlatban egy lap). A /employees BFF-végpontról fogyaszt (identity).
//
// IdP-modell: az identity GLOBÁLIS → a lista NEM app-szűrt (nincs app-választó) és NEM mutat szerep-badge-et (a
// szerep app-függő → a Jogosultságok-oldalon látszik). A MEGJELENÍTHETŐ oszlopok: Munkatárs (profilkép + név) ·
// Beosztás/Csapat (a csapat mellett a részleg semleges szövegként) · Eszközök · Státusz. Edit-vertikum: a
// ceruza sor-akció (employees.manage) a munkaviszony-adatot
// (pozíció + Discord-id) és a support-csapat-tagságokat szerkeszti (workforce/employee-details + support-team-members).
// CREATE (tiszta globális IdP-provisioning) és DELETE (globális offboarding — a személy a teljes cégből kilép) az Odin
// identity-admin fölött: GLOBÁLIS identity-életciklus → az `iam:provision` scope-hoz kötött, ezért az UI-ban arra
// kapuzva (nem hamis affordancia). A meglévő identity-mag (név/e-mail) a szerkesztőben READ-ONLY. Még NINCS: üdvözlés/
// impersonate és a lap-szintű export.
import { type EmployeeRowSchema } from '@atlas/contracts';
import {
  ArrowDownTrayIcon,
  ComputerDesktopIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { EmployeeFormSheet } from '@/components/employees/employee-form-sheet';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { IdentityStatusBadge } from '@/components/shared/identity-status-badge';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListNewButton } from '@/components/shared/list-new-button';
import { EditRowAction, RowActionButton, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonCell } from '@/components/shared/person-cell';
import { TeamBadge } from '@/components/shared/team-badge';
import { employeeKeys } from '@/features/employees/api';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?edit=<id>` → a szerkesztő-sheet a megadott munkatársra nyílik (a command palette „2. rungja").
const employeesSearchSchema = z.object({
  edit: z.string().optional(),
  q: z.string().optional(),
  order: z.string().optional(),
  position: z.string().optional(),
  team: z.string().optional(),
  department: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute('/employees')({
  validateSearch: (search) => employeesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: EmployeesRoute,
});

// Route-wrapper: a deep-link search-paramot itt olvassuk + a param-törlést itt navigáljuk (a lista-oldal router-hook-mentes).
function EmployeesRoute(): JSX.Element {
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/employees',
      search: (prev) => ({ ...prev, edit: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <EmployeesPage deepLinkEditId={edit} onClearDeepLink={clearDeepLink} />;
}

type EmployeeRow = z.infer<typeof EmployeeRowSchema>;

const PAGE_SIZE = 100;
/** A facet-szűrők „mind" pszeudó-értéke (mint az accounts/clients oldalon). */
const ALL = '__all__';

export interface EmployeesPageProps {
  /** Deep-link: a `?edit=` paraméterből érkező munkatárs-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

export function EmployeesPage({
  deepLinkEditId,
  onClearDeepLink,
}: EmployeesPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, hasScope, isLoading: permsLoading } = usePermissions();
  const canView = can('employees.view');
  // Jog-vezérelt akciók (IdP-modell): a SZERKESZTÉS (munkaviszony-adat) app-szintű workforce-jog → `employees.manage`.
  // A CREATE (tiszta globális provisioning) és a DELETE (globális offboarding) viszont GLOBÁLIS identity-életciklus,
  // amit az Odin a dedikált `iam:provision` scope-hoz köt → ezekre azt kapuzzuk (hogy ne legyen hamis affordancia).
  const canManage = can('employees.manage');
  // A CREATE és a DELETE a globális CÍMTÁR-ÉLETCIKLUS: az Odin ezt a dedikált `iam:provision` scope-hoz köti
  // (korábban `platform:admin`-hoz — emiatt a „+ Új munkatárs" gomb a MUNKATÁRS-ADMINNAK sem jelent meg,
  // pedig a művelet az ő dolga). A `platform:admin` továbbra is superuser. A feature-jog (`employees.manage`)
  // ezen KÍVÜL is kell a szerkesztéshez — az a workforce-adat joga.
  const canCreate = hasScope('iam:provision');
  const canDelete = hasScope('iam:provision');

  // Szerkesztő-sheet: `null` → zárt; egyébként a szerkesztendő munkatárs (hordozza az OCC version-t + előtöltést).
  const [editTarget, setEditTarget] = useState<EmployeeRow | null>(null);
  // Új-munkatárs sheet + törlés-megerősítés cél.
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeRow | null>(null);
  /** A köszöntő-akció megerősítése (valódi levél megy ki egy külsős/belsős postafiókba → megerősítést kér). */
  const [welcomeTarget, setWelcomeTarget] = useState<EmployeeRow | null>(null);

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    order: 'asc',
    position: ALL,
    team: ALL,
    department: ALL,
    // ÉLETCIKLUS — az ALAP az AKTÍV címtár (nem „mind"): a kivezetett kolléga külön kérésre jön elő. Mivel az
    // alapértelmezett értékű kulcs nem kerül az URL-be, a tiszta `/employees` link továbbra is tiszta marad.
    status: 'active',
  });
  const q = urlFilters.q;
  const order = urlFilters.order as 'asc' | 'desc';
  const positionFilter = urlFilters.position;
  const teamFilter = urlFilters.team;
  const departmentFilter = urlFilters.department;
  const statusFilter = urlFilters.status;
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A rendezés-kulcs fixen `name` (szerver-oldal, magyar collation) — csak az irány váltható.
  // Facet-szűrők (kliens-oldal, a betöltött soron — a belső létszám kicsi, egy lap): beosztás/csapat/részleg/státusz.

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: employees.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // A rendezés + a pozíció/részleg-nevek LOCALE-tudatosak (HU→vezetéknév elöl; EN→keresztnév elöl) → a nyelv a
  // query-kulcs része, hogy nyelvváltáskor újratöltsön.
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  // Az ÉLETCIKLUS a lekérdezés-kulcs RÉSZE: szerver-oldali szűrő, tehát külön cache-bejegyzést kap (különben
  // a váltás a régi halmazt mutatná, amíg a háttérben frissül).
  const filters = { q, order, lang, status: statusFilter };
  // GLOBÁLIS lista (IdP-modell) — nincs app-scope a kulcsban.
  const listKey = [...employeeKeys.list(filters)];

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.employees.query({
        query: {
          q: q || undefined,
          order,
          lang,
          status:
            statusFilter === 'all' ? 'all' : statusFilter === 'archived' ? 'archived' : 'active',
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.employees : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` munkatársra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<EmployeeRow>({
    id: deepLinkEditId,
    rows,
    getId: (e) => e.id,
    onOpen: (e) => setEditTarget(e),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A query nem dob non-2xx-re, csak státuszt ad → a lapok státuszából olvassuk ki a hiba-állapotot.
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  // 401/403 → jog-hiba (általános „nincs jogod" üzenet); minden más hiba → általános betöltési hiba + retry.
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));

  // Facet-opciók a betöltött (teljes belső) sorokból — distinct, magyar collation szerint rendezve.
  const collator = useMemo(() => new Intl.Collator('hu', { sensitivity: 'base' }), []);
  const distinctSorted = useCallback(
    (vals: (string | null)[]): string[] =>
      [...new Set(vals.filter((v): v is string => !!v))].sort((a, b) => collator.compare(a, b)),
    [collator],
  );
  // A beosztás-szűrő NÉVRE illeszt, és ez TUDATOS: a beosztás-név csak csapaton belül egyedi, tehát két
  // csapat azonos nevű szerepe EGY szűrő-elembe olvad, és mindkettő emberét hozza („minden Software
  // Engineer"). Aki egy KONKRÉT csapat szerepét keresi, a csapat-szűrővel metszi — a kettő együtt mindkét
  // kérdésre válaszol, és a lista cellája amúgy is kiírja a csapatot a beosztás mellé.
  const positionOptions = useMemo(
    () => distinctSorted(rows.map((r) => r.position)),
    [rows, distinctSorted],
  );
  const teamOptions = useMemo(
    () => distinctSorted(rows.map((r) => r.team)),
    [rows, distinctSorted],
  );
  const departmentOptions = useMemo(
    () => distinctSorted(rows.map((r) => r.department)),
    [rows, distinctSorted],
  );
  // ÉLETCIKLUS-OPCIÓK — FIX lista, NEM a betöltött sorokból származtatva.
  //
  // ⚠️ MIÉRT NEM FACET (mért hiba): a korábbi `distinctSorted(rows.map(r => r.status))` csak azokat az
  // értékeket kínálta, amik az ÉPP BETÖLTÖTT sorokban előfordultak — és mivel minden élő munkatárs `active`,
  // a szűrő „mind + aktív"-ot mutatott. A kivezetett halmaz ebből SOHA nem állt elő: nem az opció hiányzott,
  // hanem a sorok, amikből az opció származott volna. A szűrés ezért mostantól SZERVER-OLDALI.
  const STATUS_OPTIONS = ['active', 'archived'] as const;

  // Kliens-oldali szűrés a facet-értékek szerint (a lista kicsi, teljesen betöltve).
  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (positionFilter === ALL || r.position === positionFilter) &&
          (teamFilter === ALL || r.team === teamFilter) &&
          (departmentFilter === ALL || r.department === departmentFilter),
      ),
    [rows, positionFilter, teamFilter, departmentFilter],
  );

  const hasFilters =
    q !== '' ||
    positionFilter !== ALL ||
    teamFilter !== ALL ||
    departmentFilter !== ALL ||
    // az alap `active` is szűkítés (Andi #95) — a „mind" az egyetlen szűretlen állapot
    statusFilter !== 'all';

  // Egyetlen rendezhető oszlop (név): a fejléc-kattintás CSAK az irányt váltja (a kulcs fix `name`).
  const toggleOrder = useCallback(
    () => setFilters({ order: order === 'asc' ? 'desc' : 'asc' }),
    [order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés = GLOBÁLIS offboarding (optimista lista-sor eltávolítás + rollback, a billing-minta). A BFF az Odin
  // `DELETE /v1/admin/identities/:id`-t hívja → a személy a teljes cégből kilép; siker → a sor eltűnik, hiba → visszagördül.
  type ListPage = Awaited<ReturnType<typeof api.employees.query>>;
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.employeeDelete.mutation({ params: { id } }),
    invalidateKeys: [employeeKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        const id = deleteTarget?.id;
        if (!old || !id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    employees: p.body.employees.filter((e) => e.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:employees.deleted'),
      conflict: t('toast:employees.deleteError'),
      forbidden: t('toast:employees.forbidden'),
      validation: t('toast:employees.validation'),
      error: t('toast:employees.deleteError'),
    },
  });
  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
    }
  }, [deleteTarget, removeMutation]);

  /**
   * ÜDVÖZLŐ LEVÉL (a legacy manuális welcome-gombja). NEM `useEntityMutation`: az nincs mit optimistán írni a
   * lista-cache-be (a levél nem módosít sort), és nincs 409/422 ág sem — így egy sima mutáció + toast a helyes
   * alak. A siker `202` = „elindítottuk" (a kézbesítés fire-and-forget), ezért a toast is ezt fejezi ki.
   */
  const welcomeMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => api.employeeSendWelcome.mutation({ params: { id } }),
    onSuccess: (res) => {
      // A ts-rest a 4xx-et NEM dobja → a státuszt itt bontjuk. A hibaüzenet SOHA nem szivárogtat jog-nevet:
      // a szerver generikus szöveget ad, mi pedig a saját i18n-kulcsunkat mutatjuk.
      if (res.status === 202) toast.success(t('toast:employees.welcomeSent'));
      // 409 = a cél NEM kaphat meghívót (az Odin `issued: false` ága: nincs aktív app-tagsága, inaktív a fiókja,
      // governance-admin vagy service-fiók). Ez NEM néma siker: kiírjuk, hogy az admin tudjon mit tenni.
      else if (res.status === 409) toast.error(t('toast:employees.welcomeNotEligible'));
      else if (res.status === 403) toast.error(t('toast:employees.forbidden'));
      else toast.error(t('toast:employees.welcomeError'));
    },
    onError: () => toast.error(t('toast:employees.welcomeError')),
  });
  const confirmWelcome = useCallback(() => {
    if (welcomeTarget) {
      welcomeMutation.mutate({ id: welcomeTarget.id }, { onSettled: () => setWelcomeTarget(null) });
    }
  }, [welcomeTarget, welcomeMutation]);

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;

  // Sor-akciók: ceruza → munkaviszony-adat + support-tagság szerkesztése (employees.manage; az identity-mag
  // név/e-mail a sheetben READ-ONLY), inaktiválás (employees.delete). A create a lap-szintű ListNewButton.
  const rowActions = (e: EmployeeRow): JSX.Element => {
    // KIVEZETETT SOR: a műveletek NEM a `status`-ra, hanem az ÉLETCIKLUSRA kapuznak. A kivezetett
    // munkatársnak nincs élő munkaviszony-adata (az offboarding azt is soft-delete-eli), tehát a szerkesztő
    // üres űrlapot nyitna és a mentése elhasalna; az újra-offboarding pedig 404-et adna. Hamis affordancia
    // helyett a sor ilyenkor CSAK olvasható — az archív nézet a múltat mutatja, nem munkaterület.
    // `!= null` (LAZA) SZÁNDÉKOSAN: a hiányzó mező (régi cache-elt válasz, teszt-fixture) `undefined` — azt
    // ÉLŐNEK vesszük. A szigorú `!== null` az ismeretlen életciklust „kivezetett"-nek minősítené, és némán
    // eltüntetné a sor-műveleteket MINDEN élő soron (mérve: a három route-teszt így bukott el).
    const offboarded = e.offboardedAt != null;
    return (
      <RowActions>
        {canManage && !offboarded && (
          <EditRowAction label={t('employees.edit')} onClick={() => setEditTarget(e)} />
        )}
        {canManage && !offboarded && (
          <RowActionButton
            label={t('employees.welcome')}
            icon={EnvelopeIcon}
            onClick={() => setWelcomeTarget(e)}
          />
        )}
        {/* MEGSZEMÉLYESÍTÉS: SZÁNDÉKOSAN NINCS a munkatárs-soron (2026-09-23). A képesség PARTNER-ONLY lett —
          az Odin a belsős célt 403-cal utasítja el (`impersonation_target_not_partner`), tehát a gomb hamis
          affordancia lenne. MIÉRT: a VÉDETT jogok (per-user kiosztás, szerephez nem köthető — ma a
          Bevétel/kiadás hármas) birtokosának nevében belépve a „csak birtokostól, magadnak soha" fal
          megkerülhető lenne. A belsős diagnózist a jogosultság-konzol és az eseménynapló szolgálja ki.
          A gomb a Kapcsolattartók lapon (partner) ÉL. */}
        {canDelete && !offboarded && (
          // INAKTIVÁLÁS (Andi #96): nem törlés — ezért nem kuka ikon, hanem „felhasználó eltávolítása";
          // a megerősítő ablak mondja ki, mi szűnik meg és mi marad meg
          <RowActionButton
            label={t('employees.delete')}
            icon={UserMinusIcon}
            destructive
            onClick={() => setDeleteTarget(e)}
          />
        )}
      </RowActions>
    );
  };

  // EXPORT: a sorok a lista-végpontról, az AKTÍV keresővel/rendezéssel, 200-as lapokkal a végéig.
  const fetchExportPage = useCallback(
    async (offset: number | undefined) => {
      const r = await api.employees.query({
        query: { q: q || undefined, order, lang, limit: 200, offset: offset ?? 0 },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.employees, next: r.body.nextOffset };
    },
    [q, order, lang],
  );

  const columns: DataTableColumn<EmployeeRow>[] = [
    {
      // 1. Munkatárs — profilkép + név + e-mail halvány másodlagos sorban. Az e-mail a névhez tartozó identity-adat →
      // a klasszikus „identity-cella" minta (nincs külön e-mail oszlop). Szerep-badge NINCS (app-függő → Jogosultságok).
      id: 'name',
      header: t('employees.columns.employee'),
      sortable: true,
      cardLabel: t('employees.columns.employee'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      // A cella két adatot mutat (locale-tudatos név + e-mail) → az exportban ugyanez, sortöréssel.
      exportValue: (e) =>
        [
          formatName(
            {
              givenName: e.givenName,
              familyName: e.familyName,
              displayName: e.displayName ?? e.email,
            },
            i18n.language,
          ),
          e.email,
        ]
          .filter(Boolean)
          .join('\n'),
      cell: (e) => (
        <PersonCell
          givenName={e.givenName}
          familyName={e.familyName}
          name={e.displayName ?? e.email}
          avatarUrl={e.avatarUrl}
          accentColor={e.teamColor}
          secondary={e.email ?? emDash}
        />
      ),
    },
    {
      // 2. Beosztás / Csapat — a workforce `positions`/`teams` (a legacy 2. oszlopa, most a részleg→csapat→beosztás
      // hierarchiához igazítva). A pozíció a fő sor, a csapat a globális TeamBadge (MORGENS-paletta; ugyanaz a
      // szín, mint az avatar-háttéren — vizuális összekötés). A RÉSZLEG (a csapat felettese) SZÁNDÉKOSAN NINCS
      // a sorban: a badge mellé írva zsúfolt volt, és a kérdés itt az, hogy KI ez és MIT csinál — a részleg
      // csoportosítás, nem azonosság. Szűrni továbbra is lehet rá: külön facet a toolbarban.
      id: 'position',
      header: t('employees.columns.position'),
      cardLabel: t('employees.columns.position'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (e) => [e.position, e.team, e.department].filter(Boolean).join(' · '),
      // Mobil-kártya: a 2. sor (subtitle) — a beosztás/csapat a kulcs másodlagos azonosító.
      card: 'subtitle',
      cell: (e) => {
        if (!e.position && !e.team) return emDash;
        return (
          <div className="flex min-w-0 flex-col items-start gap-1">
            {/* A pozíció a DataTable alap cella-stílusát ÖRÖKLI (text-muted-foreground) — egyezik a többi 2./3.
                oszloppal (nincs foreground `Text`-felülírás). A csapat a globális TeamBadge (soft tónus). */}
            {e.position && <div className="max-w-full truncate">{e.position}</div>}
            <TeamBadge name={e.team} color={e.teamColor} className="max-w-full truncate" />
          </div>
        );
      },
    },
    {
      // 3. Ügyfélszolgálat — mely support-csapatoknak tagja a kolléga (a legacy „Ügyfélszolgálat" oszlopa).
      // CSAK MEGJELENÍTÉS: a tagságot a Support-csapatok oldalon szerkesztjük (egy szerkesztő-felület).
      id: 'supportTeams',
      header: t('employees.columns.supportTeams'),
      cardLabel: t('employees.columns.supportTeams'),
      width: 'minmax(0,1.6fr)',
      multiline: true,
      // Mobil-kártya: REJTVE — a kártyán a beosztás/részleg viszi a subtitle-t (egy kibontó/kártya a szabály).
      card: 'hidden',
      cell: (e) =>
        e.supportTeams.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<{ id: string; name: string }>
            items={e.supportTeams}
            getKey={(team) => team.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('employees.moreSupportTeams', { count: n })}
            triggerAriaLabel={t('employees.moreSupportTeamsAria')}
            renderItem={(team) => <span className="block min-w-0 truncate">{team.name}</span>}
          />
        ),
      exportValue: (e) => e.supportTeams.map((team) => team.name).join(', '),
    },
    {
      // 4. Eszközök — a workforce/assets, holder=identity (a legacy 4. oszlopa). Kis kategória-ikon (hardware =
      // monitor-ikon, software = grid-ikon) + eszköz-név; 3+ eszköznél legördülő (ExpandableCell, mint a
      // fiókok/ügyfelek oldalon). A név → eszköz-szerkesztő LINK MAJD (az oldal még nincs → egyelőre sima szöveg).
      id: 'assets',
      header: t('employees.columns.assets'),
      cardLabel: t('employees.columns.assets'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (e) => e.assets.map((a) => a.name).join('\n'),
      // Mobil-kártya: REJTVE — másodlagos lista-cella (ExpandableCell); a kártyán a név + beosztás + státusz a lényeg.
      card: 'hidden',
      cell: (e) =>
        e.assets.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={e.assets}
            getKey={(a) => a.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('employees.moreAssets', { count: n })}
            triggerAriaLabel={t('employees.moreAssetsAria')}
            renderItem={(a) => (
              <span className="flex min-w-0 items-center gap-1.5">
                {a.category === 'software' ? (
                  <Squares2X2Icon className="size-3.5 shrink-0 opacity-70" aria-hidden />
                ) : (
                  <ComputerDesktopIcon className="size-3.5 shrink-0 opacity-70" aria-hidden />
                )}
                <span className="truncate">{a.name}</span>
              </span>
            )}
          />
        ),
    },
    {
      // 4. Státusz — Odin identity-státusz (a legacy usernek nem volt státusza; Atlas-oldali dúsítás).
      id: 'status',
      header: t('employees.columns.status'),
      cardLabel: t('employees.columns.status'),
      width: 'minmax(0,1fr)',
      exportValue: (e) => t(`employees.statusLabel.${e.status}`, { defaultValue: e.status }),
      // Badge-cella: `multiline` → overflow-hidden (NEM truncate) → keskenyítéskor NINCS „…" a pill után.
      multiline: true,
      cell: (e) => (
        <IdentityStatusBadge
          status={e.status}
          label={t(`employees.statusLabel.${e.status}`, { defaultValue: e.status })}
        />
      ),
    },
  ];

  const exportCtl = useListExport<EmployeeRow, number>({
    columns,
    slug: 'employees',
    fetchPage: fetchExportPage,
    filters: { q },
  });

  return (
    <AppShell
      secondaryActions={[
        // MOBIL: a sáv export-gombja el van rejtve (`hidden md:block`) — a FAB-ban viszont elérhető marad.
        // FORMÁTUM-VÁLASZTÁS NÉLKÜL, `xlsx`-re: a FAB egy koppintásos felület, egy legördülő a legördülőben
        // zsákutca lenne; az `xlsx` a leggyakoribb cél (a deszktop-gomb továbbra is mind a hármat kínálja).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
      ]}
      toolbar={
        // KÖZÖS ListToolbar: kereső (név/e-mail) + facet-szűrők (beosztás/részleg/státusz) + darabszám-meta —
        // ugyanaz a globális komponens, mint a fiókok/ügyfelek oldalon.
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'position',
              label: t('employees.filterPosition'),
              value: positionFilter,
              onChange: (v: string) => setFilters({ position: v }),
              options: positionOptions.map((o) => ({ value: o, label: o })),
              allValue: ALL,
              // TULAJDONNÉV-lista (a beosztás-nevek ADATBÓL jönnek) → nem kisbetűsítjük.
              preserveCase: true,
            },
            {
              key: 'team',
              label: t('employees.filterTeam'),
              value: teamFilter,
              onChange: (v: string) => setFilters({ team: v }),
              options: teamOptions.map((o) => ({ value: o, label: o })),
              allValue: ALL,
              preserveCase: true,
            },
            {
              key: 'department',
              label: t('employees.filterDepartment'),
              value: departmentFilter,
              onChange: (v: string) => setFilters({ department: v }),
              options: departmentOptions.map((o) => ({ value: o, label: o })),
              allValue: ALL,
              preserveCase: true,
            },
            {
              key: 'status',
              label: t('employees.filterStatus'),
              value: statusFilter,
              onChange: (v: string) => setFilters({ status: v }),
              options: STATUS_OPTIONS.map((s) => ({
                value: s,
                label: t(`employees.statusLabel.${s}`, { defaultValue: s }),
              })),
              // a „mind" a lista TETEJÉN; az alap `active` is aktív szűrő — a badge jelzi, a „Szűrők törlése"
              // a „mind"-re állít (Andi #95)
              allValue: 'all',
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('employees.loading')
              : !isError && !infinite.isLoading
                ? t('employees.countOf', { shown: filteredRows.length, total })
                : ''
          }
          actions={
            // A doboz MOBILON `display: contents` (mért hiba, 375 px): flexként üresen is elvitte a sáv
            // térközét, tehát a kereső + „Szűrők" sor a jobb szél ELŐTT ért véget.
            <div className="contents md:flex md:items-center md:gap-2">
              <div className="hidden md:block">
                <ListExportButton
                  onExport={exportCtl.run}
                  pending={exportCtl.pending}
                  hasRows={!isError && rows.length > 0}
                />
              </div>
              {/* CREATE (globális provisioning) — az `iam:provision` scope-ra kapuzva; a gomb csak akkor
                  látszik, ha a művelet tényleg megy (nincs hamis affordancia). */}
              {canCreate && (
                <div className="hidden md:block">
                  <ListNewButton
                    label={t('employees.new')}
                    onClick={() => setCreateOpen(true)}
                    enabled={canCreate}
                    disabledTooltip={t('common.noCreatePermission')}
                  />
                </div>
              )}
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<EmployeeRow>
          columns={columns}
          rows={filteredRows}
          getRowId={(e) => e.id}
          sort="name"
          order={order}
          onSort={toggleOrder}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('employees.noMatches')}
                description={t('employees.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('employees.empty')} description={t('employees.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('employees.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'}
          rowActions={canManage || canDelete ? rowActions : undefined}
        />
      </div>

      {/* Create (employee=null) + Edit (employee=row) — külön instance, a billing-minta szerint. */}
      <EmployeeFormSheet open={createOpen} onOpenChange={setCreateOpen} employee={null} />
      <EmployeeFormSheet
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        employee={editTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('employees.deleteTitle')}
        description={t('employees.deleteDesc', {
          name: deleteTarget
            ? formatName(
                {
                  givenName: deleteTarget.givenName,
                  familyName: deleteTarget.familyName,
                  displayName: deleteTarget.displayName ?? deleteTarget.email,
                },
                i18n.language,
              )
            : '',
        })}
        confirmLabel={t('employees.delete')}
        cancelLabel={t('employees.cancel')}
        pendingLabel={t('employees.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />

      {/* Üdvözlő levél megerősítése — a szöveg KIMONDJA, hogy jelszót nem küldünk, csak beállító linket. */}
      <ConfirmDialog
        open={welcomeTarget !== null}
        onOpenChange={(open) => !open && setWelcomeTarget(null)}
        title={t('employees.welcomeConfirmTitle')}
        description={t('employees.welcomeConfirmDesc', {
          name: welcomeTarget
            ? formatName(
                {
                  givenName: welcomeTarget.givenName,
                  familyName: welcomeTarget.familyName,
                  displayName: welcomeTarget.displayName ?? welcomeTarget.email,
                },
                i18n.language,
              )
            : '',
        })}
        confirmLabel={t('employees.welcomeConfirm')}
        cancelLabel={t('employees.cancel')}
        pending={welcomeMutation.isPending}
        onConfirm={confirmWelcome}
      />
    </AppShell>
  );
}
